import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Check, MousePointerClick, UserPlus, LogIn, Lightbulb, TrendingUp, Link2, Info, Save, Globe, QrCode, Download, ImagePlus, Trash2, Award, Pencil, X, User as UserIcon, MapPin, Phone, Mail, Users, Send, MailCheck, Eye } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import { SOCIAL_NETWORKS } from "../../lib/socials";

const TYPE_LABELS = { blog: "Blog", site: "Site web", influenceur: "Influenceur", autre: "Autre" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PartnerSpace() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [socials, setSocials] = useState({});
  const [savingSoc, setSavingSoc] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState({ first_name: "", last_name: "", address: "", phone: "", email: "" });
  const [mails, setMails] = useState([]);
  const [contactFor, setContactFor] = useState(null);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);

  const load = () => {
    api.get("/partner/space").then((r) => {
      setData(r.data);
      setSocials(r.data.socials || {});
      setProfile({
        first_name: r.data.first_name || "", last_name: r.data.last_name || "",
        address: r.data.address || "", phone: r.data.phone || "", email: r.data.contact_email || "",
      });
    }).catch((e) => toast.error(apiErr(e)));
    api.get("/partner/mails").then((r) => setMails(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Bloque le scroll d'arrière-plan quand la modale de contact est ouverte.
  useEffect(() => {
    if (!contactFor) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [contactFor]);

  const openContact = (f) => { setContactForm({ subject: "", message: "" }); setContactFor(f); };
  const sendContact = async () => {
    if (!contactForm.message.trim()) return toast.error("Écrivez votre message.");
    setSendingContact(true);
    try {
      await api.post("/partner/contact", { to_email: contactFor.email, subject: contactForm.subject, message: contactForm.message });
      toast.success(`Message envoyé à ${contactFor.name}.`);
      setContactFor(null);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSendingContact(false); }
  };

  // Statut du dernier e-mail de bienvenue (envoyé / ouvert / lu).
  const welcomeMail = mails.find((m) => m.type === "partner_welcome");

  const setProf = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/partner/profile", profile);
      toast.success("Votre fiche est enregistrée.");
      setEditProfile(false);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSavingProfile(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/partner/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Votre logo est enregistré.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingLogo(false); }
  };
  const removeLogo = async () => {
    try { await api.delete("/partner/logo"); toast.success("Logo retiré."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const setSoc = (key) => (e) => setSocials({ ...socials, [key]: e.target.value });
  const saveSocials = async () => {
    setSavingSoc(true);
    try {
      const clean = Object.fromEntries(Object.entries(socials).filter(([, v]) => (v || "").trim()));
      await api.put("/partner/socials", { socials: clean });
      toast.success("Vos réseaux sociaux sont enregistrés.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSavingSoc(false); }
  };
  const filledSocials = SOCIAL_NETWORKS.filter((n) => (socials[n.key] || "").trim());

  const link = data ? `${window.location.origin}/r/${data.code}` : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch { const t = document.createElement("textarea"); t.value = link; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    setCopied(true);
    toast.success("Lien copié — diffusez-le auprès de votre audience.");
    setTimeout(() => setCopied(false), 2000);
  };

  const stats = data ? [
    { l: "Clics sur votre lien", v: data.clicks, icon: MousePointerClick, c: "text-cyan", d: "Nombre de personnes qui ont cliqué sur votre lien." },
    { l: "Inscrits", v: data.signups, icon: UserPlus, c: "text-green-400", d: "Personnes qui ont créé un compte grâce à votre lien." },
    { l: "Connectés", v: data.logins, icon: LogIn, c: "text-electric", d: "Inscrits parrainés qui se sont connectés à leur espace." },
    { l: "Idées déposées", v: data.ideas, icon: Lightbulb, c: "text-amber-400", d: "Inscrits parrainés qui ont déposé une idée d'application." },
  ] : [];

  return (
    <div className="grain min-h-screen bg-void" data-testid="partner-space">
      <header className="border-b border-white/10 bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Logo size={26} />
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-ink-secondary sm:block" data-testid="partner-identity">{user?.email}</span>
            <button onClick={() => { logout(); window.location.href = "/connexion"; }} data-testid="partner-logout"
              className="rounded-full border border-white/15 px-4 py-2 text-xs text-white transition-colors hover:border-cyan">Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Espace partenaire</div>
        <h1 className="mt-2 font-display text-3xl font-900 text-white">
          {data ? data.name : "Votre activité"}
          {data?.ptype && <span className="ml-3 rounded-full bg-cyan/15 px-3 py-1 align-middle text-[11px] font-600 uppercase text-cyan">{TYPE_LABELS[data.ptype] || data.ptype}</span>}
        </h1>
        {data?.rank && (() => {
          const r = data.rank;
          const tot = data.total_partners || 0;
          const place = `N°${r}${tot ? ` sur ${tot} partenaire${tot > 1 ? "s" : ""}` : ""}`;
          let msg;
          let cls = "border-amber-400/30 bg-amber-400/10 text-amber-300";
          if (r === 1) {
            cls = "border-amber-400/50 bg-amber-400/15 text-amber-300";
            msg = tot > 1
              ? `Vous êtes en tête (${place}) ! Bravo — conservez votre avance en partageant votre lien.`
              : `Vous êtes ${place} ! Partagez votre lien pour faire grandir votre communauté.`;
          } else if (r <= 3) {
            cls = "border-cyan/40 bg-cyan/10 text-cyan";
            msg = `Vous êtes ${place} — sur le podium ! Encore un effort pour viser la 1re place.`;
          } else {
            msg = `Vous êtes ${place} — partagez votre lien pour grimper au classement.`;
          }
          return (
            <div className={`mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-600 ${cls}`} data-testid="partner-rank">
              <Award size={15} /> {msg}
            </div>
          );
        })()}

        {/* Statut de l'e-mail de bienvenue : envoyé / ouvert / lu */}
        {welcomeMail && (
          <div className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-white/10 bg-surface px-4 py-1.5 text-xs" data-testid="partner-welcome-mail-status">
            <span className="inline-flex items-center gap-1.5 text-ink-secondary"><Mail size={13} className="text-cyan" /> E-mail de bienvenue :</span>
            <span className={`inline-flex items-center gap-1 font-600 ${welcomeMail.status === "envoye" ? "text-green-400" : "text-ink-tertiary"}`}>
              <Send size={11} /> {welcomeMail.status === "envoye" ? "Envoyé" : welcomeMail.status === "non_configure" ? "Non configuré" : "Non envoyé"}
            </span>
            <span className={`inline-flex items-center gap-1 font-600 ${welcomeMail.opened_at ? "text-cyan" : "text-ink-tertiary"}`}>
              <Eye size={11} /> {welcomeMail.opened_at ? `Ouvert le ${new Date(welcomeMail.opened_at).toLocaleDateString("fr-FR")}` : "Pas encore ouvert"}
            </span>
            <span className={`inline-flex items-center gap-1 font-600 ${welcomeMail.read_at ? "text-green-400" : "text-ink-tertiary"}`}>
              <MailCheck size={11} /> {welcomeMail.read_at ? `Lu le ${new Date(welcomeMail.read_at).toLocaleDateString("fr-FR")}` : "Pas encore lu"}
            </span>
          </div>
        )}

        {/* Explications */}
        <div className="mt-6 rounded-2xl border border-cyan/25 bg-cyan/[0.06] p-5" data-testid="partner-explainer">
          <div className="flex items-center gap-2 text-sm font-600 text-white"><Info size={16} className="text-cyan" /> Comment ça marche ?</div>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-ink-secondary">
            <li>Copiez votre <span className="text-white">lien unique</span> ci-dessous et diffusez-le (article, post, bio, story…).</li>
            <li>Chaque personne qui clique arrive sur la page d'inscription : le <span className="text-white">clic</span> est compté.</li>
            <li>Si elle crée un compte, l'<span className="text-white">inscription</span> vous est rattachée automatiquement.</li>
            <li>Suivez ici en temps réel vos <span className="text-white">clics, inscrits, connexions et idées déposées</span>.</li>
          </ul>
        </div>

        {/* Lien unique + QR code */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-link-card">
          <div className="flex items-center gap-2 text-sm font-600 text-white"><Link2 size={16} className="text-cyan" /> Votre lien à diffuser</div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-white/10 bg-void px-4 py-3 font-mono text-sm text-cyan" data-testid="partner-link">{link}</code>
                <button onClick={copy} data-testid="partner-copy-link"
                  className="inline-flex items-center gap-2 rounded-lg bg-cyan px-4 py-3 text-sm font-600 text-void transition-all hover:glow-cyan">
                  {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copié" : "Copier"}
                </button>
              </div>
            </div>
            {data && (
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-void p-3">
                <img src={`${BACKEND}/api/partners/${data.id}/qrcode.png`} alt="QR code du lien" className="h-16 w-16 rounded-lg bg-white p-1" data-testid="partner-qr-img" />
                <a href={`${BACKEND}/api/partners/${data.id}/qrcode.png`} download={`qrcode-${data.code}.png`} data-testid="partner-qr-download"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs text-white transition-colors hover:border-cyan">
                  <Download size={14} /> QR code
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Ma fiche partenaire : photo centrée en haut + infos complètes (fermée par défaut, modifiable via un bouton) */}
        {data && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-fiche-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-600 text-white"><UserIcon size={16} className="text-cyan" /> Ma fiche partenaire</div>
              {!editProfile && (
                <button onClick={() => setEditProfile(true)} data-testid="partner-fiche-edit"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-xs font-600 text-cyan transition-all hover:bg-cyan/20">
                  <Pencil size={13} /> Modifier
                </button>
              )}
            </div>

            {/* Photo centrée en haut (modifiable à tout moment) */}
            <div className="mt-4 flex flex-col items-center">
              {data.has_logo ? (
                <img src={`${BACKEND}/api/partners/${data.id}/logo`} alt={data.name} className="h-24 w-24 rounded-full border-2 border-cyan/40 object-cover" data-testid="partner-fiche-photo" />
              ) : (
                <span className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-cyan/30 bg-cyan/10 font-display text-2xl font-700 text-cyan" data-testid="partner-fiche-photo">
                  {(data.first_name || data.name).slice(0, 1).toUpperCase()}{(data.last_name || "").slice(0, 1).toUpperCase()}
                </span>
              )}
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-cyan hover:underline" data-testid="partner-fiche-photo-upload">
                <ImagePlus size={12} /> {uploadingLogo ? "Envoi…" : data.has_logo ? "Changer la photo" : "Ajouter une photo"}
                <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
              </label>
            </div>

            {!editProfile ? (
              /* Mode fermé : toutes les infos en lecture seule */
              <div className="mt-5 space-y-2.5" data-testid="partner-fiche-view">
                <InfoRow icon={UserIcon} label="Prénom" value={data.first_name} />
                <InfoRow icon={UserIcon} label="Nom" value={data.last_name} />
                <InfoRow icon={MapPin} label="Adresse" value={data.address} />
                <InfoRow icon={Phone} label="Téléphone" value={data.phone} />
                <InfoRow icon={Mail} label="E-mail" value={data.contact_email} />
              </div>
            ) : (
              /* Mode édition */
              <div className="mt-5 space-y-3" data-testid="partner-fiche-form">
                <div className="grid gap-3 sm:grid-cols-2">
                  <FField label="Prénom" value={profile.first_name} onChange={setProf("first_name")} testid="pf-firstname" />
                  <FField label="Nom" value={profile.last_name} onChange={setProf("last_name")} testid="pf-lastname" />
                </div>
                <FField label="Adresse complète" value={profile.address} onChange={setProf("address")} testid="pf-address" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <FField label="Téléphone" value={profile.phone} onChange={setProf("phone")} testid="pf-phone" />
                  <FField label="E-mail" type="email" value={profile.email} onChange={setProf("email")} testid="pf-email" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveProfile} disabled={savingProfile} data-testid="partner-fiche-save"
                    className="inline-flex items-center gap-2 rounded-lg bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
                    <Save size={15} /> {savingProfile ? "Enregistrement…" : "Enregistrer"}
                  </button>
                  <button onClick={() => { setEditProfile(false); load(); }} data-testid="partner-fiche-cancel"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white transition-colors hover:border-white/40">
                    <X size={15} /> Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Réseaux sociaux du partenaire (saisie manuelle, cliquables si renseignés) */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-socials-card">
          <div className="flex items-center gap-2 text-sm font-600 text-white"><Globe size={16} className="text-cyan" /> Mes réseaux sociaux</div>
          <p className="mt-1 text-xs text-ink-secondary">Renseignez vos URL ; seuls les réseaux remplis s'affichent et sont cliquables.</p>
          {filledSocials.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="partner-socials-active">
              {filledSocials.map((n) => (
                <a key={n.key} href={socials[n.key]} target="_blank" rel="noopener noreferrer" title={n.label} data-testid={`partner-social-${n.key}`}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan/30 bg-cyan/10 text-cyan transition-all hover:border-cyan hover:glow-cyan">
                  <n.icon size={16} />
                </a>
              ))}
            </div>
          )}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SOCIAL_NETWORKS.map((n) => (
              <label key={n.key} className="flex items-center gap-2 rounded-lg border border-white/10 bg-void px-3 py-2">
                <n.icon size={14} className={(socials[n.key] || "").trim() ? "text-cyan" : "text-ink-tertiary"} />
                <input value={socials[n.key] || ""} onChange={setSoc(n.key)} placeholder={n.label} data-testid={`partner-input-${n.key}`}
                  className="w-full bg-transparent text-xs text-white outline-none placeholder:text-ink-tertiary" />
              </label>
            ))}
          </div>
          <button onClick={saveSocials} disabled={savingSoc} data-testid="partner-save-socials"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
            <Save size={15} /> {savingSoc ? "Enregistrement…" : "Enregistrer mes réseaux"}
          </button>
        </div>

        {/* Statistiques */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/10 bg-surface p-5" data-testid={`partner-stat-${s.l}`}>
              <s.icon size={20} className={s.c} />
              <div className="mt-3 font-display text-3xl font-900 text-white tabular-nums">{s.v}</div>
              <div className="mt-1 text-xs font-600 text-white">{s.l}</div>
              <div className="mt-1 text-[11px] leading-snug text-ink-tertiary">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Taux de conversion */}
        {data && (
          <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-surface px-5 py-4" data-testid="partner-conversion">
            <TrendingUp size={16} className="text-cyan" />
            <span className="text-sm text-ink-secondary">Taux de conversion (clics → inscrits) :</span>
            <span className="font-display text-lg font-700 text-cyan">{data.conversion} %</span>
          </div>
        )}

        {/* Ma communauté : nom + comportement de chaque filleul */}
        {data && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-filleuls">
            <div className="flex items-center gap-2 text-sm font-600 text-white"><Users size={16} className="text-cyan" /> Ma communauté — membres inscrits ({(data.filleuls || []).length})</div>
            <p className="mt-1 text-xs text-ink-secondary">Suivez le comportement de chaque personne inscrite via votre lien (inscription, connexions, idées déposées).</p>
            {(data.filleuls || []).length === 0 ? (
              <p className="mt-3 text-sm italic text-ink-tertiary">Aucun inscrit pour le moment. Partagez votre lien pour faire grandir votre communauté.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-[10px] uppercase tracking-wide text-ink-tertiary">
                      <th className="px-3 py-2 font-600">Membre de votre communauté</th>
                      <th className="px-3 py-2 font-600">Inscrit le</th>
                      <th className="px-3 py-2 font-600">Dernière connexion</th>
                      <th className="px-3 py-2 text-center font-600">Connexions</th>
                      <th className="px-3 py-2 font-600">Idées déposées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.filleuls || []).map((f, i) => (
                      <tr key={i} className="border-b border-white/5" data-testid={`filleul-row-${i}`}>
                        <td className="px-3 py-2.5">
                          <div className="font-600 text-white">{f.name}</div>
                          <div className="text-[11px] text-ink-tertiary">{f.email}</div>
                          <button onClick={() => openContact(f)} data-testid={`filleul-contact-${i}`}
                            className="mt-1 inline-flex items-center gap-1 rounded-full border border-cyan/30 bg-cyan/10 px-2.5 py-1 text-[10px] font-600 text-cyan transition-colors hover:border-cyan">
                            <Mail size={10} /> Contacter
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-ink-secondary">{f.registered_at ? new Date(f.registered_at).toLocaleDateString("fr-FR") : "—"}</td>
                        <td className="px-3 py-2.5 text-xs text-ink-secondary">{f.last_login ? new Date(f.last_login).toLocaleDateString("fr-FR") : "Jamais"}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-600 ${f.logins > 0 ? "bg-green-500/15 text-green-400" : "bg-white/5 text-ink-tertiary"}`}>{f.logins}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          {f.ideas > 0 ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-600 text-amber-300">
                              <Lightbulb size={11} /> {f.ideas} {(f.idea_titles || [])[0] ? `· ${(f.idea_titles || [])[0]}` : ""}
                            </span>
                          ) : (
                            <span className="text-[11px] italic text-ink-tertiary">Aucune</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* Modale : écrire à un membre de la communauté */}
        {contactFor && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" data-testid="contact-modal" onClick={() => setContactFor(null)}>
            <div className="mx-auto my-10 w-full max-w-lg rounded-2xl border border-white/12 bg-surface p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="flex items-center gap-2 font-display text-lg font-700 text-white"><Mail size={17} className="text-cyan" /> Écrire à {contactFor.name}</h3>
                <button onClick={() => setContactFor(null)} data-testid="contact-close" className="rounded-lg p-1.5 text-ink-tertiary hover:bg-white/5 hover:text-white"><X size={17} /></button>
              </div>
              <div className="mt-4 space-y-3">
                <p className="text-xs text-ink-secondary">Le message part de l'adresse officielle APP CHALLENGE ACADEMY, en votre nom de partenaire, vers <span className="text-white">{contactFor.email}</span>. L'ouverture et la lecture sont suivies.</p>
                <label className="block">
                  <span className="mb-1 block text-[11px] uppercase tracking-wide text-white">Objet</span>
                  <input value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} data-testid="contact-subject"
                    placeholder={`Message de ${data?.name || "votre partenaire"}`}
                    className="w-full rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] uppercase tracking-wide text-white">Message</span>
                  <textarea value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} data-testid="contact-message" rows={5}
                    placeholder="Bonjour, merci de nous avoir rejoint…"
                    className="w-full resize-none rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
                </label>
                <div className="flex gap-2 pt-1">
                  <button onClick={sendContact} disabled={sendingContact} data-testid="contact-send"
                    className="inline-flex items-center gap-2 rounded-lg bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
                    <Send size={15} /> {sendingContact ? "Envoi…" : "Envoyer"}
                  </button>
                  <button onClick={() => setContactFor(null)} data-testid="contact-cancel"
                    className="rounded-lg border border-white/15 px-4 py-2.5 text-sm text-white hover:border-white/40">Annuler</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-void px-4 py-2.5">
      <Icon size={14} className="shrink-0 text-cyan" />
      <span className="w-24 shrink-0 text-[11px] uppercase tracking-wide text-ink-tertiary">{label}</span>
      <span className={`truncate text-sm ${value ? "text-white" : "italic text-ink-tertiary"}`}>{value || "Non renseigné"}</span>
    </div>
  );
}

function FField({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
    </label>
  );
}
